import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { getSelectedBusinessData } from "selectors/businesses";
import { getTextsData } from "selectors/texts";
import { useDispatch, useSelector } from "react-redux";
import ActionCell from "./lib/ActionCell";
import Arrays from "utils/Arrays";
import Constants from "const/Constants";
import DataStatuses from "const/DataStatuses";
import DateTimeFormats from "const/DateTimeFormats";
import DocumentIdCell from "nlib/pages/DocumentsPage/lib/DocumentsTable/lib/DocumentIdCell";
import DocumentPaymentTypes from "const/DocumentPaymentTypes";
import DocumentRecogniseStatuses from "const/DocumentRecogniseStatuses";
import DocumentsStatuses from "nlib/pages/DocumentsPage/DocumentsStatuses";
import IntegrationServices from "const/IntegrationServices";
import MainApiActions from "actions/MainApiActions";
import Money from "utils/Money";
import React, { useCallback } from "react";
import Table, { TableCell, TableHead, TableRow } from "nlib/ui/Table";
import TypeCell from "nlib/pages/DocumentsPage/lib/DocumentsTable/lib/TypeCell";
import UiActions from "actions/UiActions";
import classNames from "classnames";
import moment from "moment";

const DocumentsTable = ({
  data = [],
  title,
  baseDocumentTable,
  sortBy,
  sortOrder,
  className,
  onSortChange,
  titleClassName
}) => {
  const dispatch = useDispatch();

  const { uiTexts } = useSelector(getTextsData);

  const selectedBusinessData = useSelector(getSelectedBusinessData);

  const { extraData: { integrationService } = {} } = selectedBusinessData;

  const { logo: serviceLogo } = IntegrationServices.getByValue(integrationService) || {};

  const handlePreview = useCallback(async(id) => {
    const { recogniseData: { previewFile } = {} } = Arrays.findById(data, id);

    const link = await dispatch(MainApiActions.fetchAttachmentUrl(previewFile));

    if (!link) return;
    dispatch(UiActions.showModalImages([link]));
  }, [data, dispatch]);

  return (
    <Table
      theme="flat"
      className={classNames(Css.documentsTable, className)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortChange={onSortChange}>
      {!!onSortChange && (
        <TableRow>
          <TableHead className={Css.statusCell}>{uiTexts.status}</TableHead>
          <TableHead className={Css.typeCell}>{uiTexts.type}</TableHead>
          <TableHead className={Css.idCell}>{`${uiTexts.document} #`}</TableHead>
          <TableHead className={Css.amountCell}>{uiTexts.total}</TableHead>
          <TableHead className={Css.addressCell}>{uiTexts.payee}</TableHead>
          <TableHead className={Css.issueDateCell}>{uiTexts.issueDate}</TableHead>
          <TableHead className={Css.lineItemsCell}>{uiTexts.description}</TableHead>
          <TableHead className={Css.actionsCell}>{uiTexts.actions}</TableHead>
        </TableRow>
      )}
      {!!title && <div className={classNames(Css.title, titleClassName)}>{title}</div>}
      {data.map((documentData) => {
        const {
          id,
          paid,
          status,
          address,
          currency,
          issueDate,
          lineItems,
          amountVat,
          amountBase,
          paymentType,
          recogniseData = {}
        } = documentData;

        const {
          labelLangId,
          iconComponent: StatusIcon,
          errorIconComponent: ErrorStatusIcon
        } = DocumentsStatuses.getStatusData(status);

        const errorState = recogniseData.status === DocumentRecogniseStatuses.ERROR;

        const buyPayment = paymentType === DocumentPaymentTypes.BUY;

        const amountValue = (+amountBase || 0) + (+amountVat || 0);

        const amountContent = Money.toMoneyString(amountValue, currency);

        const Icon = (() => {
          if (errorState) return ErrorStatusIcon;
          if (status === DataStatuses.EXPORTED) return () => <img src={serviceLogo} className={Css.serviceLogo} />;

          return StatusIcon;
        })();

        const statusTitle = (() => {
          if (errorState) return uiTexts.error;
          if (status === DataStatuses.EXPORTED) {
            if (paid) return uiTexts.paid;

            return uiTexts.unpaid;
          }

          return uiTexts[labelLangId];
        })();

        const lineItemsContent = lineItems && lineItems.map(({ description, quantity }) => {
          return `${description} ${quantity ? `[x${quantity || 1}]` : ""}`.trim();
        }).join("; ");

        return (
          <TableRow key={id} className={classNames(Css.tableRow, baseDocumentTable && Css.warning)}>
            <TableCell className={Css.statusCell}>
              <div title={statusTitle} data-status={errorState ? recogniseData.status : status}>
                <Icon />
              </div>
            </TableCell>
            <TableCell className={Css.typeCell}>
              <TypeCell documentData={documentData} />
            </TableCell>
            <TableCell className={Css.idCell}>
              <DocumentIdCell documentData={documentData} />
            </TableCell>
            <TableCell className={Css.amountCell}>
              <div
                title={amountContent}
                className={classNames(Css.amount, paymentType && Css.coloredNumber)}
                data-negative={buyPayment ? "" : undefined}>
                {amountContent}
              </div>
            </TableCell>
            <TableCell className={Css.addressCell}>
              {address?.name
                ? <div title={address.name}>
                  <Icons.User />
                  <span>{address.name}</span>
                </div>
                : <div>{Constants.EMPTY_PLACEHOLDER}</div>}
            </TableCell>
            <TableCell className={Css.issueDateCell}>
              {issueDate ? moment.utc(issueDate).format(DateTimeFormats.DATE_TEXT) : Constants.EMPTY_PLACEHOLDER}
            </TableCell>
            <TableCell className={Css.lineItemsCell}>
              <div title={lineItemsContent || ""}>{lineItemsContent || Constants.EMPTY_PLACEHOLDER}</div>
            </TableCell>
            <TableCell className={classNames(Css.actionsCell, Css.tableBodyCell)}>
              <ActionCell disabled={!recogniseData.previewFile} id={id} onPreview={handlePreview} />
            </TableCell>
          </TableRow>
        );
      })}
    </Table>
  );
};

export default React.memo(DocumentsTable);
